import _ from 'lodash'
import {
  ColDef,
  Column,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  IRowNode,
  MenuItemDef,
  RowDataUpdatedEvent,
  RowGroupOpenedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { PageArea } from '..'
import { AllState } from '../../../store'
import { ROW_HEIGHT } from '../../containers/BulkSheet'
import MemberWorkloadStatussHeader from './components/Header'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { DateTerm } from '../../../utils/date'
import { pageComponent } from '../../higher-order-components/pageComponent'
import { BulkSheetView } from '../../containers/BulkSheetView'
import {
  isWorkMonthColumn,
  refreshDynamicColumnDef,
  memberWorkloadStatusGridOptions,
} from './gridOptions'
import {
  collapseAllRows,
  expandAllRows,
} from '../../containers/BulkSheetView/gridOptions/contextMenu'
import { useMemberWorkloadStatusData } from './hooks/memberWorkloadStatusData'
import { useMasterData } from './hooks/masterData'
import { StoredPageState, usePageState } from './hooks/pageState'
import { InputError } from '../../containers/BulkSheetView/lib/validation'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import { intl } from '../../../i18n'
import {
  ColumnQuickFilterKey,
  MemberWorkloadStatusRow,
  MemberWorkloadStatusProps,
  fetchAlertThresholds,
  saveAlertThresholds,
  toPercentage,
  toDecimal,
} from './MemberWorkloadStatus'
import Loading from '../../components/process-state-notifications/Loading'
import { SavedUIState } from '../../components/dialogs/SavedUIStateDialog/SavedUIStateList'
import { useBulkSheetState } from './hooks/bulkSheetState'
import { useExcel } from './hooks/excel'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SPACE } from '../../model/keyBind'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { dateVoService } from '../../../domain/value-object/DateVO'
import { OrganizationWorkingDayCalendarDetail } from '../../../lib/functions/organizationWorkingDayCalendar'
import { ResourcePlanExcelExportDialog } from './components/MemberWorkloadStatusExcelExportDialog'
import DateVO from '../../../vo/DateVO'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import { AlertThreshold } from './components/HighlightSettingButton'

const MemberWorkloadStatus = (props: MemberWorkloadStatusProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortedColumnStates, setSortedColumnStates] = useState<
    SortedColumnState[]
  >([])
  const [openUiState, setOpenUiState] = useState<boolean>(false)
  const [initializedGridOptions, setInitializedGridOptions] =
    useState<boolean>(false)
  const [alertThreshold, setAlertThreshold] = useState<
    AlertThreshold | undefined
  >()

  useEffect(() => {
    ;(async () => {
      const response = await fetchAlertThresholds()
      setAlertThreshold(toPercentage(response))
    })()
  }, [])

  const handleChangeAlertThreshold = useCallback(
    async (threshold: AlertThreshold) => {
      await saveAlertThresholds(toDecimal(threshold))
      setAlertThreshold(threshold)
    },
    []
  )
  const [needsRestoration, setNeedsRestoration] = useState<boolean>(false)
  const expandedRowUuids = useRef<Set<string>>(new Set())
  const collapsedRowUuids = useRef<Set<string>>(new Set())

  const pageState = usePageState(props.uuid)
  const prevPageState = useRef<StoredPageState>({
    aggregateTarget: pageState.aggregateTarget,
    workloadUnit: pageState.workloadUnit,
    timeGrain: pageState.timeGrain,
    assignmentType: pageState.assignmentType,
    fromDate: '',
    toDate: '',
    rowHeight: ROW_HEIGHT.SMALL,
  })
  const { workingDays } = useMasterData({
    startDate: pageState.fromDate,
    endDate: pageState.toDate,
  })
  const prevWorkingDays =
    useRef<OrganizationWorkingDayCalendarDetail[]>(workingDays)

  const { rowData, fetchRecords, applyFilter } = useMemberWorkloadStatusData()

  const recreateGrid = useCallback(() => {
    setInitializedGridOptions(true)
  }, [])

  const destroyGrid = useCallback(() => {
    setInitializedGridOptions(false)
    // Recreate after DOM rewriting is complete (setTimeout 0sec)
    setTimeout(() => recreateGrid(), 0)
  }, [recreateGrid])

  const gridOptions: GridOptions = useMemo(() => {
    // Re-create the Grid as needed
    destroyGrid()
    return memberWorkloadStatusGridOptions()
  }, [destroyGrid])

  useEffect(() => {
    gridOptions.api?.destroy()
  }, [gridOptions])

  const excel = useExcel(gridOptions)
  const columnSetting = useColumnSetting()
  const bulkSheetState = useBulkSheetState(
    gridOptions,
    props.uuid,
    UiStateKey.ResourcePlanCrossProjectsMemberView
  )

  const workloadUnitState = useWorkloadUnit(pageState.workloadUnit)

  useEffect(() => {
    gridOptions.context = {
      ...gridOptions.context,
      workloadUnit: pageState.workloadUnit,
      workloadUnitState,
      timeGrain: pageState.timeGrain,
      alertThreshold,
      assignmentType: pageState.assignmentType,
      aggregateTarget: pageState.aggregateTarget,
    }
    gridOptions.api?.refreshCells({ force: true })
  }, [
    pageState.workloadUnit,
    workloadUnitState,
    pageState.timeGrain,
    pageState.assignmentType,
    pageState.aggregateTarget,
    gridOptions,
    alertThreshold,
  ])

  const refreshAll = useCallback(async () => {
    setLoading(true)
    await fetchRecords(
      {
        startDate: pageState.fromDate,
        endDate: pageState.toDate,
      },
      pageState.aggregateTarget,
      pageState.timeGrain,
      pageState.assignmentType
    )
    bulkSheetState.restoreExpandedRows()
    gridOptions.context = {
      ...gridOptions.context,
      draggableNodeId: undefined,
      errors: new InputError(),
      assignmentType: pageState.assignmentType,
      aggregateTarget: pageState.aggregateTarget,
    }
    setLoading(false)
  }, [fetchRecords, gridOptions, bulkSheetState, pageState])

  const onReload = useCallback(() => {
    refreshAll()
  }, [refreshAll])

  const onChangeColumnFilter = useCallback(
    (value: ColumnQuickFilterKey) => {
      if (value === ColumnQuickFilterKey.INITIAL) {
        gridOptions.columnApi?.resetColumnState()
        gridOptions.api?.setFilterModel(null)
        gridOptions.api?.onFilterChanged()
      } else if (value === ColumnQuickFilterKey.RESTORE) {
        setOpenUiState(true)
      }
    },
    [gridOptions.api, gridOptions.columnApi]
  )

  const onChangeDateTerm = useCallback(
    (term: DateTerm) => {
      if (!!term.startDate) {
        pageState.setFromDate(term.startDate)
      }
      if (!!term.endDate) {
        pageState.setToDate(term.endDate)
      }
      const states = gridOptions?.columnApi
        ?.getColumns()
        ?.filter((c: Column) => {
          if (c.isVisible()) return false

          const parent = c.getParent()
          if (!parent || !isWorkMonthColumn(parent.getGroupId())) return false

          return dateVoService.isBetween(
            dateVoService.construct(c.getColId()),
            dateVoService.construct(term.startDate),
            dateVoService.construct(term.endDate),
            '[]'
          )
        })
        .map(c => ({
          colId: c.getColId(),
          hide: false,
        }))
      gridOptions?.columnApi?.applyColumnState({ state: states })
    },
    [pageState, gridOptions.columnApi]
  )

  const onDeletedFilterColumn = useCallback(
    (column: ColDef) => {
      if (!column || !gridOptions.api) return
      const key = column.colId || column.field
      if (!key) return
      const filterModel = gridOptions.api.getFilterModel()
      delete filterModel[key]
      gridOptions.api.setFilterModel(filterModel)
    },
    [gridOptions.api]
  )

  const resetFilters = useCallback(() => {
    if (!gridOptions.api) return
    gridOptions.api.setFilterModel([])
  }, [gridOptions.api])

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<any>) => {
      if (!gridOptions.columnApi) return
      gridOptions.columnApi?.applyColumnState({
        state: [{ colId: colId.toString(), sort: null }],
      })
    },
    [gridOptions]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridOptions.columnApi) return
    gridOptions.columnApi?.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridOptions])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridOptions.columnApi) return
      gridOptions.columnApi?.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridOptions]
  )

  const getDisplayColumnName = useCallback(
    (colDef: ColDef) => {
      if (!gridOptions.columnApi) return
      const column = gridOptions.columnApi?.getColumn(colDef.colId)
      const parentColumnDef = column?.getParent()?.getColGroupDef()
      if (parentColumnDef && isWorkMonthColumn(parentColumnDef.groupId)) {
        return new DateVO(column!.getColId()).format(
          intl.formatMessage({ id: 'dateFormat.yyyy.mm' })
        )
      }
      return colDef.headerName
    },
    [gridOptions.columnApi]
  )

  const contextMenu = useCallback(
    (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
      return [expandAllRows(params), collapseAllRows(params)].filter(
        m => !!m
      ) as (string | MenuItemDef)[]
    },
    []
  )

  const onRowDataUpdated = useCallback(
    (e: RowDataUpdatedEvent<MemberWorkloadStatusRow>) => {
      e.api?.refreshCells({ force: true })
    },
    []
  )

  const onFirstDataRendered = useCallback(
    e => {
      bulkSheetState.onFirstDataRendered(e)
      setLoading(false)
    },
    [bulkSheetState]
  )

  const onGridReady = useCallback(async () => {
    const result = await refreshDynamicColumnDef({
      api: gridOptions.api,
      dateTerm: {
        startDate: pageState.fromDate,
        endDate: pageState.toDate,
      },
      workingDays,
      timeGrain: pageState.timeGrain,
    })

    await refreshAll()

    if (result.workingDayCalendars) {
      gridOptions.context = {
        ...gridOptions.context,
        workingDayCalendars: result.workingDayCalendars,
        timeGrain: pageState.timeGrain,
        assignmentType: pageState.assignmentType,
        aggregateTarget: pageState.aggregateTarget,
      }
    }

    bulkSheetState.onGridReady()
  }, [gridOptions, pageState, workingDays, bulkSheetState, refreshAll])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filter = bulkSheetState.onFilterChanged(e)
      setFilteredColumns(filter)

      const filterModel = gridOptions.api?.getFilterModel()
      const bodyTypeFilter = filterModel?.['body.type']

      if (bodyTypeFilter && bodyTypeFilter.values) {
        const selectedValues = bodyTypeFilter.values as string[]

        const resourcePlanLabel = intl.formatMessage({
          id: 'memberWorkload.status.resourcePlan',
        })
        const estimatedLabel = intl.formatMessage({
          id: 'memberWorkload.status.estimated',
        })
        const actualLabel = intl.formatMessage({
          id: 'memberWorkload.status.actual',
        })

        const filterState = {
          resourcePlan: selectedValues.includes(resourcePlanLabel),
          estimated: selectedValues.includes(estimatedLabel),
          actual: selectedValues.includes(actualLabel),
        }

        applyFilter(filterState)
      } else {
        applyFilter({
          resourcePlan: true,
          estimated: true,
          actual: true,
        })
      }

      setNeedsRestoration(true)
    },
    [bulkSheetState, gridOptions.api, applyFilter]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      const sorted = bulkSheetState.onSortChanged(e)
      setSortedColumnStates(sorted.filter((v): v is SortedColumnState => !!v))
    },
    [bulkSheetState]
  )

  const getRowHeight = useCallback((): number => {
    return pageState.rowHeight
  }, [pageState.rowHeight])

  const restoreExpandedState = useCallback(() => {
    if (!gridOptions.api) return

    bulkSheetState.restoreExpandedRows()

    const tryRestoreExpandedState = (
      sourceUuid: string,
      node: IRowNode<MemberWorkloadStatusRow>
    ): boolean => {
      if (expandedRowUuids.current.has(sourceUuid)) {
        node.setExpanded(true)
        return true
      }
      if (collapsedRowUuids.current.has(sourceUuid)) {
        node.setExpanded(false)
        return true
      }
      return false
    }

    gridOptions.api.forEachNode(node => {
      const nodeUuid = node.data?.uuid
      if (!nodeUuid) return

      if (tryRestoreExpandedState(nodeUuid, node)) return

      if (nodeUuid.endsWith(':total:estimated')) {
        const memberUuid = nodeUuid.replace(':total:estimated', '')
        if (tryRestoreExpandedState(memberUuid, node)) return
      }

      const totalEstimatedUuid = `${nodeUuid}:total:estimated`
      if (tryRestoreExpandedState(totalEstimatedUuid, node)) return

      if (nodeUuid.endsWith(':estimated') && nodeUuid.includes(':project:')) {
        const projectUuid = nodeUuid.replace(':estimated', '')
        if (tryRestoreExpandedState(projectUuid, node)) return
      }

      if (nodeUuid.match(/^[^:]+:project:[^:]+$/)) {
        const projectEstimatedUuid = `${nodeUuid}:estimated`
        if (tryRestoreExpandedState(projectEstimatedUuid, node)) return
      }
    })
  }, [gridOptions.api, bulkSheetState])

  const closeUiStateDialog = useCallback(() => {
    setOpenUiState(false)
  }, [])

  const onSelectUiState = useCallback(
    (uiState: SavedUIState) => {
      const { columnState, filterState } = uiState.UIState
      if (columnState) {
        bulkSheetState.base.saveColumnState(columnState)
        gridOptions.columnApi?.applyColumnState({
          state: columnState,
          applyOrder: true,
        })
      }
      if (filterState) {
        bulkSheetState.base.saveFilterState(filterState)
        gridOptions.api?.setFilterModel(filterState)
      }
      closeUiStateDialog()
    },
    [bulkSheetState, closeUiStateDialog, gridOptions.api, gridOptions.columnApi]
  )

  const isConditionChanged = useCallback(() => {
    if (
      !prevPageState.current ||
      prevPageState.current.aggregateTarget !== pageState.aggregateTarget ||
      prevPageState.current.timeGrain !== pageState.timeGrain ||
      prevPageState.current.assignmentType !== pageState.assignmentType ||
      prevPageState.current.fromDate !== pageState.fromDate ||
      prevPageState.current.toDate !== pageState.toDate ||
      !_.isEqual(prevWorkingDays.current, workingDays)
    ) {
      prevPageState.current = {
        aggregateTarget: pageState.aggregateTarget,
        workloadUnit: pageState.workloadUnit,
        timeGrain: pageState.timeGrain,
        assignmentType: pageState.assignmentType,
        fromDate: pageState.fromDate,
        toDate: pageState.toDate,
        rowHeight: pageState.rowHeight,
      }
      prevWorkingDays.current = workingDays
      return true
    }
    return false
  }, [
    pageState.aggregateTarget,
    pageState.workloadUnit,
    pageState.timeGrain,
    pageState.assignmentType,
    pageState.fromDate,
    pageState.toDate,
    pageState.rowHeight,
    workingDays,
  ])

  useEffect(() => {
    if (isConditionChanged() && pageState.initialized) {
      ;(async () => {
        const result = await refreshDynamicColumnDef({
          api: gridOptions.api,
          dateTerm: {
            startDate: pageState.fromDate,
            endDate: pageState.toDate,
          },
          workingDays,
          timeGrain: pageState.timeGrain,
        })

        await refreshAll()

        if (result.workingDayCalendars) {
          gridOptions.context = {
            ...gridOptions.context,
            workingDayCalendars: result.workingDayCalendars,
            timeGrain: pageState.timeGrain,
            assignmentType: pageState.assignmentType,
            aggregateTarget: pageState.aggregateTarget,
          }
        }
      })()
    }
  }, [
    isConditionChanged,
    refreshAll,
    gridOptions,
    pageState.fromDate,
    pageState.toDate,
    pageState.timeGrain,
    workingDays,
    pageState.initialized,
    pageState.assignmentType,
    pageState.aggregateTarget,
  ])

  useEffect(() => {
    if (!needsRestoration || !gridOptions.api) return

    requestAnimationFrame(() => {
      restoreExpandedState()
      setNeedsRestoration(false)
    })
  }, [rowData, needsRestoration, gridOptions.api, restoreExpandedState])

  useEffect(() => {
    gridOptions.api?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent) => {
      bulkSheetState.onRowGroupOpened(event)

      const uuid = event.node.data?.uuid
      if (!uuid) return

      if (event.expanded) {
        expandedRowUuids.current.add(uuid)
        collapsedRowUuids.current.delete(uuid)
      } else {
        expandedRowUuids.current.delete(uuid)
        collapsedRowUuids.current.add(uuid)
      }
    },
    [bulkSheetState]
  )

  const onSpaceKeyDown = useCallback(() => {
    const { api } = gridOptions
    const focusedCell = api?.getFocusedCell()
    const focusedNode = api?.getDisplayedRowAtIndex(focusedCell?.rowIndex ?? -1)
    if (
      !focusedCell ||
      !focusedNode ||
      !document.activeElement!.classList.contains('ag-cell')
    ) {
      return
    }
    focusedNode.setExpanded(!focusedNode.expanded)
  }, [gridOptions])

  useKeyBind([
    {
      key: KEY_SPACE,
      fn: onSpaceKeyDown,
      stopDefaultBehavior: true,
    },
  ])

  if (!pageState.initialized) {
    return <></>
  }

  // This process is to solve the problem that GridOptions does not switch.
  if (!initializedGridOptions) {
    return <></>
  }

  return (
    <PageArea>
      <MemberWorkloadStatussHeader
        onChangeAggregateTarget={pageState.setAggregateTarget}
        aggregateTarget={pageState.aggregateTarget}
        onChangeWorkloadUnit={pageState.setWorkloadUnit}
        workloadUnit={pageState.workloadUnit}
        timeGrain={pageState.timeGrain}
        onChangeTimeGrain={pageState.setTimeGrain}
        assignmentType={pageState.assignmentType}
        onChangeAssignmentType={pageState.setAssignmentType}
        fromDate={pageState.fromDate}
        toDate={pageState.toDate}
        onChangeDateTerm={onChangeDateTerm}
        onReload={onReload}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeletedFilterColumn}
        resetFilters={resetFilters}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        sortColumnsState={sortedColumnStates}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={pageState.rowHeight}
        onChangeHeight={pageState.setRowHeight}
        alertThreshold={alertThreshold}
        onChangeAlertThreshold={handleChangeAlertThreshold}
        onClickExportExcel={excel.openDialog}
        getDisplayColumnName={getDisplayColumnName}
      />
      <BulkSheetView
        gridOptions={gridOptions}
        getContextMenuItems={contextMenu}
        getRowHeight={getRowHeight}
        onColumnMoved={bulkSheetState.rememberColumnState}
        onColumnResized={bulkSheetState.rememberColumnState}
        onColumnVisible={bulkSheetState.rememberColumnState}
        onFilterChanged={onFilterChanged}
        onFirstDataRendered={onFirstDataRendered}
        onGridReady={onGridReady}
        onRowDataUpdated={onRowDataUpdated}
        onRowGroupOpened={onRowGroupOpened}
        onSortChanged={onSortChanged}
        ref={ref}
        rowData={rowData}
      />
      <Loading isLoading={loading} elem={ref.current} />
      <SavedUIStateDialog
        applicationFunctionUuid={props.uuid}
        open={openUiState}
        title={intl.formatMessage({
          id: 'savedUIState.BULK_SHEET_UI_STATE_COLUMN_AND_FILTER',
        })}
        uiStateKey={bulkSheetState.columnSettingUiStateKey}
        sharable={false}
        currentUIState={{
          columnState: bulkSheetState.base.columnState,
          filterState: bulkSheetState.base.filterState,
        }}
        onSelect={onSelectUiState}
        onClose={closeUiStateDialog}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        columnApi={gridOptions.columnApi ?? undefined}
        gridApi={gridOptions.api ?? undefined}
        height={ref.current?.offsetHeight}
        openSavedUiStateDialog={() => setOpenUiState(true)}
        initializeColumnState={() =>
          onChangeColumnFilter(ColumnQuickFilterKey.INITIAL)
        }
        applicationFunctionUuid={props.uuid}
        uiStateKey={bulkSheetState.columnSettingUiStateKey}
        columnState={bulkSheetState.base.columnState}
        offset={90}
      />
      {excel.dialogOpen && (
        <ResourcePlanExcelExportDialog
          onClose={excel.closeDialog}
          onSubmit={excel.onExportExcel}
          columnApi={gridOptions.columnApi}
        />
      )}
    </PageArea>
  )
}

const mapStateToProps = (state: AllState) => ({
  functions: state.appFunction.functions,
})

export default connect(mapStateToProps)(pageComponent(MemberWorkloadStatus))
